import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { useState, useEffect } from "react";
import axios from "axios";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Booking from "@/pages/Booking";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MaintenancePage from "@/pages/MaintenancePage";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";

const API = process.env.REACT_APP_BACKEND_URL;

// Public route wrapper that checks maintenance mode
const PublicRoute = ({ children, maintenance }) => {
  if (maintenance.enabled) {
    return <MaintenancePage message={maintenance.message} />;
  }
  return children;
};

function App() {
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "", loading: true });

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/maintenance`);
        setMaintenance({ ...res.data, loading: false });
      } catch (e) {
        setMaintenance({ enabled: false, message: "", loading: false });
      }
    };
    checkMaintenance();
    // Check every 30 seconds for updates
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  if (maintenance.loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-sage border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <LanguageProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public routes - affected by maintenance mode */}
            <Route element={<PublicRoute maintenance={maintenance}><Layout /></PublicRoute>}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/contact" element={<Contact />} />
            </Route>
            {/* Admin routes - NOT affected by maintenance mode */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            {/* Catch-all for unknown routes */}
            <Route path="*" element={
              maintenance.enabled 
                ? <MaintenancePage message={maintenance.message} /> 
                : <Navigate to="/" replace />
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </LanguageProvider>
    </div>
  );
}

export default App;
